import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import API from 'utils/request';

import { TOrbit } from '../i18n/TModules';
import { orbitTokens, orbitType } from '../styles';
import { OrbitOutcomeCard } from './OrbitHandoffCard';

/**
 * The report-download states the dashboard already treats as terminal. Lifted verbatim from
 * `containers/Report/components/DownloadReportsTable.tsx`, which polls the same record: a second,
 * divergent list here is how two surfaces end up disagreeing about whether a report is done.
 */
export const REPORT_COMPLETE_STATES = ['failed', 'completed', 'timeout', 'complete'];
export const REPORT_ERROR_STATES = ['failed', 'timeout'];

export const REPORT_POLL_INTERVAL_MS = 5000;

export type OrbitReportPhase = 'processing' | 'completed' | 'failed';

export interface IOrbitReportStatus {
  phase: OrbitReportPhase;
  /** The server's own state word for the report the turn queued. `null` until the first read. */
  state: string | null;
}

interface IReportDownloadResponse {
  data: {
    id: number;
    state: string;
  };
}

export const reportPhaseOf = (state: string | null): OrbitReportPhase => {
  if (state === null || !REPORT_COMPLETE_STATES.includes(state)) {
    return 'processing';
  }

  return REPORT_ERROR_STATES.includes(state) ? 'failed' : 'completed';
};

/**
 * Follows ONE queued report to its terminal state.
 *
 * `GET /v4/dash/report_downloads/:id` is the per-id read the Report module's own table already uses
 * for exactly this (`DownloadReportsTable.tsx` `reDownload`), so the list is not fetched and
 * filtered here: the turn carries the id.
 *
 * Orbit does not create, download or retry the report — FR §4.13 leaves all three with the
 * Reporting Service and the Reports module. This reads a status and stops.
 */
export const useReportDownloadStatus = (
  reportDownloadId: number | null,
  intervalMs: number = REPORT_POLL_INTERVAL_MS
): IOrbitReportStatus => {
  const [state, setState] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    setState(null);

    if (reportDownloadId === null) {
      return undefined;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const read = (): void => {
      API.get<IReportDownloadResponse>(`/v4/dash/report_downloads/${reportDownloadId}`)
        .then(response => {
          if (stopped || !mounted.current) {
            return;
          }

          const next = response.data?.data?.state ?? null;
          setState(next);

          if (next === null || !REPORT_COMPLETE_STATES.includes(next)) {
            timer = setTimeout(read, intervalMs);
          }
        })
        .catch(() => {
          /*
            A read that failed is not a failed REPORT: the request may have been rate-limited or the
            token refreshed mid-flight. The poll keeps running and the card stays in Processing,
            which is the honest reading until the server says otherwise.
          */
          if (!stopped && mounted.current) {
            timer = setTimeout(read, intervalMs);
          }
        });
    };

    read();

    return () => {
      stopped = true;

      if (timer !== null) {
        clearTimeout(timer);
      }
    };
  }, [intervalMs, reportDownloadId]);

  return { phase: reportPhaseOf(state), state };
};

// Where a queued export lands: `containers/Report` mounts `downloads` under the `reports/*` route
// of the protected shell (`containers/App/pages/index.tsx`).
export const REPORT_DOWNLOADS_PATH = '/p/reports/downloads';

/**
 * code-box `5032:10505`: 436.5 x 38.4, r8, `#05090F` under a `#212D4B` stroke, its line inset 19
 * from the left and 12.8 from the top at Regular 11 `#268AFF`. Desktop-13's Processing state.
 */
const Console = styled.p`
  margin: 0;
  padding: 12.8px 19px;
  border: 1px solid ${orbitTokens.border};
  border-radius: 8px;
  background: ${orbitTokens.surfaceConsole};
  color: ${orbitTokens.action};
  ${orbitType.helping}
`;

/** BUTTON `5032:11697`: 158 x 29 r8 `#4F46E5`, label `View Generated Report` Inter 13 white. */
const ViewButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 158px;
  height: 29px;
  padding: 0 8px;
  border-radius: 8px;
  background: #4f46e5;
  color: ${orbitTokens.launcherCard};
  ${orbitType.action}
  font-weight: 400;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    color: ${orbitTokens.launcherCard};
  }

  &:focus-visible {
    outline: 2px solid ${orbitTokens.action};
    outline-offset: 2px;
  }
`;

export interface IOrbitReportStatusProps {
  /** `report_download_id` off the turn; `null` while the Reporting Service has not issued one. */
  reportDownloadId: number | null;
  pollIntervalMs?: number;
}

/**
 * The governed report journey as FR §4.13 states it and Desktop-13/14 draw it: Processing, then
 * Success or Failure, and only on success a View/Open in Reports route.
 *
 * Before this, a `report_queued` turn drew ONE line and never moved (PLA-9563 §5) — the console line
 * while `report_download_id` was null, a static "being prepared" line once it was set, and nothing
 * that ever said the report had finished or failed. The id is now followed to its terminal state.
 */
const OrbitReportStatus: React.FC<IOrbitReportStatusProps> = ({
  reportDownloadId,
  pollIntervalMs
}) => {
  const { t } = useMultiLanguageTranslation();
  const { phase } = useReportDownloadStatus(reportDownloadId, pollIntervalMs);

  if (reportDownloadId === null || phase === 'processing') {
    return (
      <Console data-testid="orbit-answer-report-generating" data-phase="processing">
        {t(`${TOrbit}.answer.reportGenerating`)}
      </Console>
    );
  }

  if (phase === 'failed') {
    return (
      <OrbitOutcomeCard
        tone="negative"
        top={t(`${TOrbit}.report.failedTop`)}
        body={t(`${TOrbit}.report.failedBody`)}
        testId="orbit-answer-report-failed"
        topTestId="orbit-answer-report-failed-top"
        stepTestId="orbit-answer-report-failed-step"
      />
    );
  }

  return (
    <OrbitOutcomeCard
      top={t(`${TOrbit}.report.successTop`)}
      body={t(`${TOrbit}.report.successBody`)}
      testId="orbit-answer-report-success"
      topTestId="orbit-answer-report-success-top"
      stepTestId="orbit-answer-report-success-step"
      action={
        <ViewButton
          to={REPORT_DOWNLOADS_PATH}
          data-report-download-id={reportDownloadId}
          data-testid="orbit-answer-report-link"
        >
          {t(`${TOrbit}.answer.reportLink`)}
        </ViewButton>
      }
    />
  );
};

export default OrbitReportStatus;
